/**
 * Asocia la etiqueta de un envoltorio de campo con el control que rotula.
 *
 * 🔴 Existe por un defecto medido: cuatro archivos del producto definen su
 * propio componente de campo (`FormField`, `Field`) que dibuja un `<label>` y
 * después el control como HERMANO, sin `htmlFor`. Son **77 campos** —los
 * cuatro archivos encabezan la lista de campos sin nombre accesible— y el
 * rótulo está a la vista, así que mirando la pantalla no se nota: solo falla
 * al darle foco al campo, donde un lector de pantalla anuncia «cuadro de
 * edición» y nada más.
 *
 * ⚠️ **No se resuelve envolviendo el control con el `<label>`**, que sería lo
 * más simple. El `FormField` de `ConfigPage` recibe el selector de color, que
 * son dos inputs y una fila de botones de preset: dentro de un `<label>`,
 * hacer clic en un preset activaría además la etiqueta y abriría el selector
 * nativo. Envolver asocia bien y rompe el comportamiento.
 *
 * Así que el id viaja por `cloneElement`, y **solo cuando el hijo es un
 * control nativo**. Cuando el envoltorio recibe un `<div>` con varias cosas
 * adentro, no se inventa una asociación: un `htmlFor` apuntando a un `<div>`
 * no enfoca nada y solo aparenta estar resuelto. Esos casos pasan su
 * `htmlFor` a mano.
 */
import { cloneElement, isValidElement, useId } from 'react'
import type { ReactElement, ReactNode } from 'react'

const CONTROLES = new Set(['input', 'select', 'textarea'])

type ConId = { id?: string }

export function useEtiquetaAsociada(children: ReactNode, htmlForExplicito?: string) {
  // El hook va siempre, aunque después no se use: no puede ir tras un `if`.
  const generado = useId()

  const hijo = isValidElement(children) ? (children as ReactElement<ConId>) : null
  const esControl = typeof hijo?.type === 'string' && CONTROLES.has(hijo.type)
  const idPropio = esControl ? hijo!.props.id : undefined

  // Un `id` que ya venía puesto MANDA: puede tenerlo por otra razón —un test
  // que lo busca, otra etiqueta que lo apunta— y pisarlo rompería eso.
  const htmlFor = htmlForExplicito ?? idPropio ?? (esControl ? generado : undefined)
  const campo =
    esControl && !idPropio && !htmlForExplicito && htmlFor
      ? cloneElement(hijo!, { id: htmlFor })
      : children

  return { htmlFor, campo }
}
